import React, { useState, useEffect } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import DashboardLayout from '../components/DashboardLayout';
import programService from '../services/programService';
import {
    Plus,
    Edit,
    Trash2,
    Search,
    X,
    AlertCircle,
    Loader
} from 'lucide-react';

const AdminPrograms = () => {
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentProgram, setCurrentProgram] = useState({
        university: '',
        logo: '',
        badge: '',
        badgeColor: 'bg-blue-600',
        title: '',
        tags: '', // Display as comma separated string in form
        type: '',
        duration: '',
        description: '',
        featured: false,
        programUrl: '/'
    });

    const badgeColors = [
        { name: 'Blue', value: 'bg-blue-600' },
        { name: 'Purple', value: 'bg-purple-900' },
        { name: 'Green', value: 'bg-green-600' },
        { name: 'Red', value: 'bg-red-600' },
        { name: 'Yellow', value: 'bg-yellow-500' },
        { name: 'Gray', value: 'bg-gray-600' },
    ];

    useEffect(() => {
        fetchPrograms();
    }, []);

    const fetchPrograms = async () => {
        try {
            setLoading(true);
            const data = await programService.getAllPrograms();
            setPrograms(data.data || []);
            setError('');
        } catch (err) {
            console.error('Failed to fetch programs:', err);
            setError('Failed to load programs. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCurrentProgram(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const openAddModal = () => {
        setIsEditing(false);
        setCurrentProgram({
            university: '',
            logo: '',
            badge: '',
            badgeColor: 'bg-blue-600',
            title: '',
            tags: '',
            type: '',
            duration: '',
            description: '',
            featured: false,
            programUrl: '/'
        });
        setShowModal(true);
    };

    const openEditModal = (program) => {
        setIsEditing(true);
        setCurrentProgram({
            ...program,
            tags: program.tags ? program.tags.join(', ') : ''
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Process tags from string to array
            const processedData = {
                ...currentProgram,
                tags: currentProgram.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '')
            };

            if (isEditing) {
                await programService.updateProgram(currentProgram._id, processedData);
            } else {
                await programService.createProgram(processedData);
            }

            setShowModal(false);
            fetchPrograms();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save program');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this program?')) {
            try {
                await programService.deleteProgram(id);
                fetchPrograms();
            } catch (err) {
                setError('Failed to delete program');
            }
        }
    };

    const filteredPrograms = programs.filter(program =>
        program.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        program.university.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <ProtectedRoute roles={['ADMIN']}>
            <DashboardLayout roles={['ADMIN']} title="Program Management">
                <div className="space-y-6">
                    {/* Header & Actions */}
                    <div className="flex flex-col md:flex-row justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100 gap-4">
                        <div className="relative w-full md:w-96">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search programs..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={openAddModal}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors w-full md:w-auto justify-center font-medium"
                        >
                            <Plus size={18} /> Add Program
                        </button>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-50 text-red-800 rounded-lg border border-red-100 flex items-center gap-2">
                            <AlertCircle size={20} />
                            {error}
                        </div>
                    )}

                    {/* Table View (Desktop) */}
                    <div className="hidden md:block bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
                        {loading ? (
                            <div className="p-12 flex justify-center">
                                <Loader className="animate-spin text-indigo-600" size={32} />
                            </div>
                        ) : filteredPrograms.length === 0 ? (
                            <div className="p-12 text-center text-gray-500">
                                No programs found.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Program</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badge</th>
                                            <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {filteredPrograms.map((program) => (
                                            <tr key={program._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        <div className="h-10 w-10 flex-shrink-0 mr-4 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden border border-gray-200">
                                                            {program.logo ? (
                                                                <img src={program.logo} alt="" className="h-full w-full object-contain p-1" />
                                                            ) : (
                                                                <span className="text-xs font-bold text-gray-400">LOGO</span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">{program.title}</div>
                                                            <div className="text-sm text-gray-500">{program.university}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{program.type}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{program.duration}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {program.badge && (
                                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full text-white ${program.badgeColor}`}>
                                                            {program.badge}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button onClick={() => openEditModal(program)} className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded">
                                                            <Edit size={18} />
                                                        </button>
                                                        <button onClick={() => handleDelete(program._id)} className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded">
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Mobile View */}
                    <div className="md:hidden space-y-4">
                        {loading ? (
                            <div className="p-12 flex justify-center">
                                <Loader className="animate-spin text-indigo-600" size={32} />
                            </div>
                        ) : filteredPrograms.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 bg-white rounded-xl border border-gray-200">
                                No programs found.
                            </div>
                        ) : (
                            filteredPrograms.map(program => (
                                <div key={program._id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="h-10 w-10 flex-shrink-0 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden border border-gray-200">
                                            {program.logo ? (
                                                <img src={program.logo} alt="" className="h-full w-full object-contain p-1" />
                                            ) : (
                                                <span className="text-xs font-bold text-gray-400">LOGO</span>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 leading-tight mb-1">{program.title}</h4>
                                            <p className="text-xs text-gray-500">{program.university}</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                                        <div className="bg-gray-50 p-2 rounded">
                                            <span className="block text-gray-400 text-[10px] uppercase">Type</span>
                                            {program.type}
                                        </div>
                                        <div className="bg-gray-50 p-2 rounded">
                                            <span className="block text-gray-400 text-[10px] uppercase">Duration</span>
                                            {program.duration}
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                                        {program.badge ? (
                                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full text-white ${program.badgeColor}`}>
                                                {program.badge}
                                            </span>
                                        ) : <span></span>}

                                        <div className="flex gap-2">
                                            <button onClick={() => openEditModal(program)} className="text-indigo-600 bg-indigo-50 p-1.5 rounded-lg">
                                                <Edit size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(program._id)} className="text-red-600 bg-red-50 p-1.5 rounded-lg">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Modal */}
                    {showModal && (
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4 sm:p-6 transition-all">
                            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] sm:max-h-[85vh]">
                                {/* Sticky Header */}
                                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white rounded-t-2xl z-10 sticky top-0">
                                    <h3 className="text-xl font-bold text-gray-900">
                                        {isEditing ? 'Edit Program' : 'Add New Program'}
                                    </h3>
                                    <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100">
                                        <X size={24} />
                                    </button>
                                </div>

                                {/* Scrollable Content */}
                                <div className="overflow-y-auto p-6 custom-scrollbar">
                                    <form id="programForm" onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* University & Logo */}
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">University Name <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="university"
                                                        value={currentProgram.university}
                                                        onChange={handleInputChange}
                                                        required
                                                        placeholder="e.g. IIT Madras"
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Logo URL</label>
                                                    <input
                                                        type="url"
                                                        name="logo"
                                                        value={currentProgram.logo}
                                                        onChange={handleInputChange}
                                                        placeholder="https://example.com/logo.png"
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                    />
                                                </div>
                                            </div>

                                            {/* Program Title & Type */}
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Program Title <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="title"
                                                        value={currentProgram.title}
                                                        onChange={handleInputChange}
                                                        required
                                                        placeholder="e.g. Advanced Data Science"
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Program Type <span className="text-red-500">*</span></label>
                                                    <select
                                                        name="type"
                                                        value={currentProgram.type}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                    >
                                                        <option value="">Select Type</option>
                                                        <option value="Executive PG Program">Executive PG Program</option>
                                                        <option value="Master's Degree">Master's Degree</option>
                                                        <option value="Certification">Certification</option>
                                                        <option value="Bootcamp">Bootcamp</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Duration & Program URL */}
                                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Duration <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="duration"
                                                        value={currentProgram.duration}
                                                        onChange={handleInputChange}
                                                        required
                                                        placeholder="e.g. 12 Months"
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Program Page Link</label>
                                                    <input
                                                        type="text"
                                                        name="programUrl"
                                                        value={currentProgram.programUrl}
                                                        onChange={handleInputChange}
                                                        placeholder="/services/ai-data-science"
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                    />
                                                    <p className="text-xs text-gray-400 mt-1">Internal path (e.g. /services/...) or full URL</p>
                                                </div>
                                            </div>

                                            {/* Badge Info */}
                                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Badge Text (Optional)</label>
                                                    <input
                                                        type="text"
                                                        name="badge"
                                                        value={currentProgram.badge}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g. Bestseller"
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Badge Color</label>
                                                    <select
                                                        name="badgeColor"
                                                        value={currentProgram.badgeColor}
                                                        onChange={handleInputChange}
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                    >
                                                        {badgeColors.map(color => (
                                                            <option key={color.value} value={color.value}>{color.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Tags */}
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tags</label>
                                                <input
                                                    type="text"
                                                    name="tags"
                                                    value={currentProgram.tags}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g. Placement Support, Visa Support"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50/50 focus:bg-white"
                                                />
                                                <p className="text-xs text-gray-400 mt-1">Separate multiple tags with commas</p>
                                            </div>
                                        </div>
                                    </form>
                                </div>

                                {/* Sticky Footer */}
                                <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50 rounded-b-2xl">
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white hover:border-gray-400 font-semibold transition-all shadow-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        form="programForm"
                                        className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold transition-all shadow-md hover:shadow-lg transform active:scale-95"
                                    >
                                        {isEditing ? 'Save Changes' : 'Create Program'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </DashboardLayout>
        </ProtectedRoute>
    );
};

export default AdminPrograms;
